import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@chakra-ui/react'

import {
  Loading,
  PrimaryButton,
  Separator,
} from '@enechain/ecloud-designsystem'

import { EndpointList } from '~/pages/ApiKeys/components/EndpointList'
import { useSelectedEndpoints } from '~/pages/ApiKeys/hooks/useSelectedEndpoints'

type Props = {
  title: string
  submitLabel: string
  caution?: string
  name: string
  selectedRoleNames: string[]
  isSubmitting: boolean
  onSubmit: () => void
}

const SummaryEndpoints: React.FC<{ selectedRoleNames: string[] }> = ({
  selectedRoleNames,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.form.summary',
  })
  const endpoints = useSelectedEndpoints(selectedRoleNames)

  return (
    <Flex flexDirection="column" gap="1">
      <Text textStyle="textSm" color="Text.lowEmphasis">
        {t('endpoints')}
      </Text>
      <Flex alignItems="baseline" gap="1">
        <Text textStyle="headerXL" color="Text.accentPrimary">
          {endpoints.length}
        </Text>
        <Text>{t('endpointCount')}</Text>
      </Flex>
      {endpoints.length === 0 ? (
        <Text textStyle="textSm" color="Text.lowEmphasis">
          {t('noEndpoints')}
        </Text>
      ) : (
        <Box overflowY="auto" maxHeight="220px">
          <EndpointList endpoints={endpoints} />
        </Box>
      )}
    </Flex>
  )
}

export const FormSummary: React.FC<Props> = ({
  title,
  submitLabel,
  caution,
  name,
  selectedRoleNames,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.form.summary',
  })

  return (
    <Flex
      position="sticky"
      top="4"
      flexDirection="column"
      gap="3"
      padding="4"
      borderWidth="1px"
      borderColor="Border.midEmphasis"
      borderRadius="radiiLg"
    >
      <Text textStyle="headerSm">{title}</Text>
      <Flex flexDirection="column" gap="1">
        <Text textStyle="textSm" color="Text.lowEmphasis">
          {t('name')}
        </Text>
        <Text textStyle="textMd" fontWeight="600">
          {name.trim() === '' ? '—' : name}
        </Text>
      </Flex>
      <Flex flexDirection="column" gap="1">
        <Text textStyle="textSm" color="Text.lowEmphasis">
          {t('roles')}
        </Text>
        <Text textStyle="textMd" fontWeight="600">
          {selectedRoleNames.length === 0
            ? t('noRoles')
            : selectedRoleNames.join(', ')}
        </Text>
      </Flex>
      <Separator />
      <React.Suspense fallback={<Loading />}>
        <SummaryEndpoints selectedRoleNames={selectedRoleNames} />
      </React.Suspense>
      <Separator />
      <PrimaryButton loading={isSubmitting} onClick={onSubmit}>
        {submitLabel}
      </PrimaryButton>
      {caution !== undefined && (
        <Text textStyle="textSm" color="Text.lowEmphasis" textAlign="center">
          {caution}
        </Text>
      )}
    </Flex>
  )
}
